import { ComponentType, FormEvent, KeyboardEvent, MouseEvent, ReactNode, useEffect, useState } from 'react';
import { AlertCollection } from '../../components/AlertMessages/AlertMessages';
import { useTranslation } from '../../i18n/i18n';
import { ContentCard } from '../ContentCard/ContentCard';
import { Button } from '../Form/Button';
import { onKeyPress } from '../Form/PressedEnter';
import { clone } from '../utils';
import './EditableCard.css';

type EditableCardProps<T> = {
	title: string;
	editLabel?: string;
	form: ComponentType<{
		model: T;
		onBlur?: (e: any) => void;
		onChange: (e: T) => void;
		onSubmit?: (e: T) => void;
		onKeyPress: (event: KeyboardEvent<HTMLInputElement>) => void;
		alerts?: AlertCollection;
	}>;
	model: T;
	onSubmit: (model: T) => void;
	onBlur?: (model: T) => void;
	children: ReactNode;
	alwaysOpen?: boolean;
	shouldStartOpen?: boolean;
	AlertComponent?: () => JSX.Element;
	validator?: (model: T) => boolean;
};
export function EditableCard<T>({
	title,
	editLabel,
	form,
	model,
	onSubmit,
	onBlur,
	children,
	alwaysOpen = false,
	shouldStartOpen = false,
	validator,
}: EditableCardProps<T>): JSX.Element {
	const t = useTranslation().generic;
	const [isOpen, setIsOpen] = useState(alwaysOpen || shouldStartOpen);
	const [tempModel, setTempModel] = useState<T | null>(model);
	const InputFields = form;

	useEffect(() => {
		if (shouldStartOpen) {
			setIsOpen(true);
		}
	}, [shouldStartOpen]);

	if (model === null) throw new Error('model must not be null');

	function handleOpen() {
		setTempModel(clone(model));
		setIsOpen(true);
	}

	function handleSubmit(e: FormEvent) {
		e.preventDefault();
		if (validator && !validator(tempModel!)) return;
		setTempModel(null);
		setIsOpen(false);
		onSubmit(tempModel!);
	}

	function handleCancel(e: MouseEvent) {
		setTempModel(null);
		setIsOpen(false);
		e.preventDefault();
	}

	function handleOnBlur() {
		if (onBlur) {
			onBlur(tempModel!);
		}
	}

	return (
		<div onClick={isOpen ? () => {} : handleOpen}>
			<ContentCard className="editable-card" title={title}>
				{!isOpen && (
					<Button className={`secondary ${'card-edit-button-absolute'}`} onClick={handleOpen}>
						{editLabel ?? t.edit}
					</Button>
				)}
				{isOpen && tempModel != null ? (
					<form onBlur={handleOnBlur} onSubmit={handleSubmit}>
						<InputFields
							onBlur={handleOnBlur}
							onSubmit={onSubmit}
							model={tempModel}
							onChange={(e) => setTempModel(e)}
							onKeyPress={onKeyPress}
						/>

						{!alwaysOpen && (
							<div className="card-footer">
								<Button onClick={handleSubmit}>{t.save}</Button>
								<Button onClick={handleCancel} className="secondary">
									{t.cancel}
								</Button>
							</div>
						)}
					</form>
				) : (
					children
				)}
			</ContentCard>
		</div>
	);
}
